import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { useFeatureEnabled } from "../hooks/useFeatureFlag";
import { IPartLookupResponse } from "../models/PartLookup";
import apiConfig from "../utils/apiConfig";

export default function usePartsLeadLookup(leadId?: string, vehicleId?: string, requestId?: string | null | undefined, componentName = "usePartsLeadLookup") {
  const { get, post, put } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();
  const partsLookupEnabled = useFeatureEnabled("Parts Lookup");
  const staleTime = 1000 * 60 * 60 * 1; //1 hour
  const queryKey = ["partslookup", { "lead": leadId }, { "vehicle": vehicleId }];

  const lookup = useQuery({
    queryKey,
    queryFn: () => get<IPartLookupResponse>(`/partlookup/lookup?${leadId ? `leadId=${leadId}` : ""}${vehicleId ? `&vehicleId=${vehicleId}` : ""}`),
    staleTime: staleTime,
    cacheTime: staleTime * 2,
    retry: 1,
    enabled: !!requestId && partsLookupEnabled,
  });

  const lookupMutation = useMutation(
    (skipCache: boolean = false) => {
      if (!partsLookupEnabled)
        return Promise.reject(new Error("Parts Lookup is not enabled for this customer."));

      return get<IPartLookupResponse>(`/partlookup/lookup?${leadId ? `leadId=${leadId}` : ""}${vehicleId ? `&vehicleId=${vehicleId}` : ""}&ignoreLookupId=true${skipCache ? "&skipCache=true" : ""}`);
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData(queryKey, data);

        //remove local cache of brand before invalidating
        localStorage.removeItem("customerBrand");
        localStorage.removeItem("customerBrandExp");

        queryClient.refetchQueries(["parts", "multiple"]);
        queryClient.refetchQueries(["customer", "brand"]);
        queryClient.refetchQueries(["lead"]);
        queryClient.refetchQueries(["vehicles"]);
        queryClient.refetchQueries(["parts"]);
      },
    }
  );

  return {
    lookup,
    lookupMutation
  };
}
